import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import PhoneInput from "../components/shared/phone-input";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import { formatPhoneNumberToE164 } from "../components/utls/utils";

function getTargetPath(redirectTo: string | null) {
  let targetPath = "/download-app";
  if (redirectTo) {
    if (redirectTo.startsWith("http")) {
      try {
        const url = new URL(redirectTo);
        targetPath = url.pathname + url.search + url.hash;
      } catch {
        targetPath = "/download-app";
      }
    } else {
      targetPath = redirectTo.startsWith("/") ? redirectTo : `/${redirectTo}`;
    }
  }
  return targetPath;
}

const VerifyPhonePage = () => {
  const supabase = useSupabaseClient();
  const session = useSession();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  async function sendOtp() {
    if (code) {
      setCode("");
    }
    const formattedPhone = formatPhoneNumberToE164(phone);

    const { error: phoneError } = await supabase.auth.signInWithOtp({
      phone: formattedPhone,
    });

    if (phoneError) {
      setError(phoneError.message);
    } else {
      setError(null);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const foundPhone = params.get("phone");
    const redirect = params.get("redirect");
    const source = params.get("source");
    if (foundPhone) {
      setPhone(foundPhone);
    }
    if (redirect) {
      setRedirectTo(redirect);
    }

    if (source) {
      setSource(source);
    }
  }, [window.location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const { data, error } = await supabase.auth.verifyOtp({
      phone: formatPhoneNumberToE164(phone),
      token: code,
      type: "sms",
    });
    if (error) {
      setError(error.message);
    } else if (data.session) {
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      navigate(getTargetPath(redirectTo));
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (session) {
      navigate(getTargetPath(redirectTo));
    }
  }, [session, redirectTo]);

  useEffect(() => {
    if (source === "share") {
      sendOtp();
      setSource(null);
    }
  }, [source]);

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
        <Card.Header variant="cream" className="text-center">
          <Typography variant="h3" className="text-primary mb-4">
            Verify Phone
          </Typography>
          <Typography variant="body2" className="text-gray-600 mb-2">
            Harper is your personal AI medical assistant.
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Phone verification creates your secure account, ensuring only you
            have access to your personal Harper experience.
          </Typography>
        </Card.Header>
        <Card.Body>
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
              <Typography variant="body2">{error}</Typography>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <PhoneInput
              id="phone"
              label="Phone"
              value={phone}
              onChangeText={setPhone}
              placeholder="(555) 456-7890"
              fullWidth
            />
            <TamaguiInput
              id="code"
              label="Code"
              value={code}
              onChangeText={setCode}
              placeholder="SMS code"
              fullWidth
              inputType="text"
            />
            <SubmitButton
              onSubmit={handleSubmit}
              isDisabled={!phone || !code || isSubmitting}
              isLoading={isSubmitting}
              className="w-full"
            >
              Verify
            </SubmitButton>

            <div className="text-center">
              <button
                type="button"
                onClick={sendOtp}
                disabled={!phone || isSubmitting}
                className="text-sm text-terracotta hover:text-terracotta-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Resend Code
              </button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default VerifyPhonePage;
